import { Tooltip } from '@base-ui-components/react/tooltip';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import type { FC, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ReactMarkdownOptions } from 'react-markdown/lib/react-markdown';
import { CheckIconOutline } from 'utils/icons';
import { TooltipArrowIcon } from 'utils/icons/figmaIcons';

const markdownComponents: ReactMarkdownOptions['components'] = {
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline break-word"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {children}
    </a>
  ),
  p: ({ children }) => (
    <p className="whitespace-pre-wrap mb-2 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-inside mb-2 last:mb-0">{children}</ul>
  ),
};

interface SelectableCardProps {
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  label: ReactNode;
  description?: ReactNode;
  icon?: React.ComponentType<any>;
  image?: string;
  imageAlt?: string;
  imageSize?: number | { width: number; height: number };
  media?: ReactNode;
  helper?: ReactNode;
  suggested?: boolean;
  suggestedIcon?: React.ComponentType<any>;
  suggestedText?: string;
  tooltip?: ReactNode;
  className?: string;
  children?: ReactNode;
  renderMarkdown?: boolean;
  id?: string;
  ariaLabel?: string;
  tracingEvent?: string;
}

const SelectableCard: FC<SelectableCardProps> = ({
  selected,
  disabled,
  onClick,
  label,
  description,
  icon: Icon,
  image,
  imageAlt,
  imageSize,
  media,
  helper,
  suggested,
  suggestedIcon: SuggestedIcon,
  suggestedText = 'Our guess',
  tooltip,
  className,
  children,
  renderMarkdown = false,
  id,
  ariaLabel,
  tracingEvent,
}) => {
  const size =
    typeof imageSize === 'number'
      ? { width: imageSize, height: imageSize }
      : imageSize ?? { width: 32, height: 32 };
  const buttonContent = (
    <Button
      asDefault
      tracingEvent={tracingEvent}
      onClick={onClick}
      ariaDisabled={disabled}
      id={id}
      aria-label={ariaLabel}
      className={classNames(
        'relative text-init px-4 inline-flex items-center border rounded-xl transition group w-full text-left min-h-[80px]',
        {
          'bg-primary-50 cursor-not-allowed': disabled,
          'cursor-pointer': !disabled,
          'border-[#EBE8ED]': !selected,
          'border-expenses': selected,
          'hover:border-primary-300 focus:border-primary-300 shadow-sm':
            !selected && !disabled,
        },
        className,
      )}
    >
      {selected && (
        <div className="w-6 h-6 bg-expenses rounded-full border border-expenses-100 absolute top-0 right-0 translate-x-1/2 -translate-y-1/2">
          <CheckIconOutline className="w-4.5 h-4.5 text-white abs-center" />
        </div>
      )}

      <div className="flex items-center gap-3 w-full self-stretch">
        {/* Content */}
        <div className="self-center flex-1 min-w-0 py-3">
          <div className="text-primary">
            <span className="inline">
              {label}
              {suggested && (
                <span className="inline-flex align-middle ai-guess-tag-bg-border gap-1 items-center flex-shrink-0 text-taxes px-1.5 rounded-full ml-2">
                  {SuggestedIcon && (
                    <SuggestedIcon className="w-4 h-4" viewBox="2 2 20 20" />
                  )}
                  <span className="text-xs font-semibold leading-5">
                    {suggestedText}
                  </span>
                </span>
              )}
              {helper && (
                <span className="inline-block ml-2 align-middle invisible group-hover:visible group-focus:visible">
                  {helper}
                </span>
              )}
            </span>
          </div>

          {description && (
            <div className="text-sm text-primary-600 mt-1">
              {renderMarkdown ? (
                <ReactMarkdown components={markdownComponents}>
                  {description as string}
                </ReactMarkdown>
              ) : (
                description
              )}
            </div>
          )}

          {children && <div className="mt-2">{children}</div>}
        </div>
        {/* Icon or Image */}
        {media || image || Icon ? (
          <div
            id="target"
            className="flex-shrink-0 ml-auto self-stretch w-14 relative overflow-hidden h-full"
          >
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2">
              {media ? (
                media
              ) : image ? (
                <img
                  src={image}
                  alt={imageAlt ?? 'Option image'}
                  style={size}
                  className="object-cover rounded-md mx-auto"
                />
              ) : Icon ? (
                <Icon className="w-14 h-14 mx-auto" />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip.Provider>
        <Tooltip.Root delay={0} hoverable={false}>
          <Tooltip.Trigger render={buttonContent} />
          <Tooltip.Portal>
            <Tooltip.Positioner
              sideOffset={5}
              align="center"
              side="top"
              className="z-[100]"
            >
              <Tooltip.Popup className="transition-[transform,opacity] data-[ending-style]:translate-y-2 data-[ending-style]:opacity-0 data-[instant]:duration-0 data-[starting-style]:translate-y-2 data-[starting-style]:opacity-0">
                <Tooltip.Arrow className="data-[side=bottom]:top-[-10px] data-[side=left]:right-[-10px] data-[side=left]:rotate-90 data-[side=right]:left-[-10px] data-[side=right]:-rotate-90 data-[side=top]:bottom-[-10px] data-[side=top]:rotate-180">
                  <TooltipArrowIcon className="w-4 h-4 text-[#374151]" />
                </Tooltip.Arrow>
                <div className="max-w-[16rem] text-sm font-semibold p-2 bg-[#374151] rounded-lg text-white">
                  {tooltip}
                </div>
              </Tooltip.Popup>
            </Tooltip.Positioner>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  }

  return buttonContent;
};

export default SelectableCard;
